import React, { useState, useEffect, useRef, useContext, memo } from "react";
import PropTypes from "prop-types";
import { LoggerContext } from "context/LoggerContext";

import styles from "./LazyImage.module.scss";

// Global cache for already loaded images
// Helps with pagination so images don't need to reload
const imageCache = new Set();

/**
 * LazyImage component with caching and intersection observer
 * Features adaptive loading, responsive srcset support and accessibility optimizations
 * 
 * @param {string|Object} props.src - Image URL or media object with responsive variants
 * @param {string} props.alt - Alternative text for the image (required for accessibility)
 * @param {string} props.className - Additional CSS classes for the image
 * @param {string} props.classNameWrapper - Additional CSS classes for the wrapper
 * @param {string} props.fill_mode - Object-fit mode (cover, contain, etc.)
 * @param {string} props.ratio - Aspect ratio (e.g., "16x9")
 * @param {boolean} props.round - Whether to apply rounded corners
 * @param {boolean} props.disableAbsolute - Whether to disable absolute positioning
 * @param {string} props.sizes - Sizes attribute for responsive images
 * @param {boolean} props.forceLoad - Force immediate loading without intersection observer
 */
const LazyImage = ({
	src,
	alt,
	className,
	classNameWrapper,
	fill_mode,
	ratio,
	round,
	disableAbsolute,
	sizes, 
	forceLoad = false,
}) => {
	const [loaded, setLoaded] = useState(false);
	const [isVisible, setIsVisible] = useState(forceLoad);
	const imgRef = useRef(null);
	const { logDebug } = useContext(LoggerContext);
	const isCachedRef = useRef(false);

	// URL string for caching (works with media objects too)
	const cacheKey = typeof src === 'object' ? src.mediaUrl : src;
	
	// Check if image is already in cache
	useEffect(() => {
		if (cacheKey && imageCache.has(cacheKey)) {
			isCachedRef.current = true;
			setIsVisible(true); // Make immediately visible
			setLoaded(true);    // Mark as loaded
		}
	}, [cacheKey]);

	useEffect(() => {
		// Skip observer if forceLoad is active or already in cache
		if (forceLoad || isCachedRef.current) {
			setIsVisible(true);
			return;
		}
		
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "400px" } // More generous preload zone
		);
		
		if (imgRef.current) observer.observe(imgRef.current);
		
		return () => observer.disconnect();
	}, [forceLoad]);

	const handleImageLoad = () => {
		// Add image to cache
		if (cacheKey) {
			imageCache.add(cacheKey);
		}
		setLoaded(true);
		window.dispatchEvent(new Event("resize"));
	};
	
	// Enhanced alt text handling
	const safeAlt = alt || (typeof src === "object" ? src.alt_text || "" : "");
	
	// If src is a media object, use srcset and sizes
	if (typeof src === "object" && src !== null) {
		// Convert children: width as int
		const childrenWithIntWidth = (src.childs || []).map(child => ({
			...child,
			width: parseInt(child.width, 10)
		}));
		
		// Sort ascending (smallest first)
		const sortedChildren = childrenWithIntWidth.sort((a, b) => a.width - b.width);
		
		// Create srcset string in form "url widthw, ..."
		const srcsetStr = sortedChildren
			.map(child => `${child.mediaUrl} ${child.width}w`)
			.join(", ");

		return (
			<div
				ref={imgRef}
				className={`${styles.LazyImage} position-relative ${classNameWrapper || ""}`}
				style={{ aspectRatio: ratio ? ratio.replace("x", "/") : "auto" }}
				aria-hidden={safeAlt === ""}>
				{isVisible ? (
					<>
						<img
							src={src.mediaUrl}  // Fallback: Standard image
							srcSet={srcsetStr}
							sizes={sizes || "100vw"}
							alt={safeAlt}
							className={`${className || ""} ${loaded ? styles.loaded : ""} ${styles.transitionFade} 
								${fill_mode ? `object-fit-${fill_mode}` : ""} ${round ? "rounded-4" : ""} 
								${ratio && !disableAbsolute ? "position-absolute top-0 start-0 w-100 h-100" : ""}`}
							onLoad={handleImageLoad}
							loading={isCachedRef.current ? "eager" : "lazy"}
							onError={() => logDebug("Image failed to load:", src.mediaUrl)}
							aria-hidden={safeAlt === ""} 
						/>
						{/* Placeholder during loading */}
						{!loaded && (
							<div 
								className={styles.placeholder}
								role="presentation"
								aria-hidden="true" 
							/>
						)}
					</>
				) : (
					<div 
						className={`${styles.placeholder} w-100 h-100`}
						role="presentation"
						aria-hidden="true">
						{/* Initial placeholder before image is loaded */}
					</div>
				)}
			</div>
		);
	}

	// Fallback for string-based src
	return (
		<div
			ref={imgRef}
			className={`${styles.LazyImage} position-relative ${classNameWrapper || ""}`}
			style={{ aspectRatio: ratio ? ratio.replace("x", "/") : "auto" }}
			aria-hidden={safeAlt === ""}>
			{isVisible ? (
				<>
					<img
						src={src}
						alt={safeAlt}
						className={`${className || ""} ${loaded ? styles.loaded : ""} ${styles.transitionFade} 
							${fill_mode ? `object-fit-${fill_mode}` : ""} ${round ? "rounded-4" : ""} 
							${ratio && !disableAbsolute ? "position-absolute top-0 start-0 w-100 h-100" : ""}`}
						onLoad={handleImageLoad}
						loading={isCachedRef.current ? "eager" : "lazy"}
						onError={() => logDebug("Image failed to load:", src)}
						aria-hidden={safeAlt === ""}
					/>
					{/* Placeholder during loading */}
					{!loaded && (
						<div 
							className={styles.placeholder}
							role="presentation"
							aria-hidden="true" 
						/>
					)}
				</>
			) : (
				<div 
					className={`${styles.placeholder} w-100 h-100`}
					role="presentation"
					aria-hidden="true">
					{/* Initial placeholder before image is loaded */}
				</div>
			)}
		</div>
	);
};

LazyImage.propTypes = {
	src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	alt: PropTypes.string,
	className: PropTypes.string,
	classNameWrapper: PropTypes.string,
	fill_mode: PropTypes.string,
	ratio: PropTypes.string,
	round: PropTypes.bool,
	disableAbsolute: PropTypes.bool,
	sizes: PropTypes.string,
	forceLoad: PropTypes.bool
};

// Memo to prevent unnecessary rerenders
export default memo(LazyImage);
